import { useEffect, useState, useRef } from "react";
import { deleteCrmLeadsService, deleteMultipleLeadsService, getCrmDDLService, getCrmLeadsService, updateLeadFollowUpService } from "../../../../Services/CRMAdminServices/CRMTaskListService";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { Link } from "react-router-dom";
import { Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import TextField from "../../../common/TextField";
import MobileField from "../../../common/MobileField";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import * as XLSX from "xlsx";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import BootstrapTable from "react-bootstrap-table-next";
import SelectDatePicker from "../../../common/SelectDatePicker";
import { useSelector } from "react-redux";
import { RootState } from "../../../../ReduxToolkit/Store";

const PAGE_SIZE = 50;
const LeadsProperty = () => {
    const [leads, setLeads] = useState<any>([]);
    const [totalLeads, setTotalLeads] = useState<number>(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const navigate = useSafeNavigate();
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const mountedRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [leadDropdownData, setLeadDropdownData] = useState<any>([])
    const [selectedLeadsId, setSelectedLeadsId] = useState<any>([])
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const { RoleName, RoleID, nameidentifier: userID } = useSelector((state: RootState) => state.adminAuth);
    const isAdmin = RoleName?.toLowerCase() === "admin";
    const [formData, setFormData] = useState({
        leadID: "",
        surname: "",
        mobile: "",
        email: "",
        staff: isAdmin ? "" : userID?.toString() || "",
        source: "",
        pageNumber: 1,
        pageSize: 500,   
         followup: '',
        stage: ''
    });


    const { data: leadsData, isLoading, isError, refetch } = useGetRequestQuery<any>(
        ['getCrmLeads'],
        () => getCrmLeadsService(formData) as any,
        { 
            enabled: false,
            keepPreviousData: true,
            staleTime: 30000,
            cacheTime: 60000,
        }
    );

    useEffect(() => {
        if (!isAdmin && userID && !mountedRef.current) {
            setFormData(prev => ({ ...prev, staff: userID.toString() }));
        }
        if (!mountedRef.current) {
            mountedRef.current = true;
            refetch();
        }
    }, [isAdmin, userID]);


    useEffect(() => {
        if (leadsData && !isError) {
            const scrollPos = scrollContainerRef.current?.scrollTop || 0;
            const leadsResult = leadsData?.data || [];
            setLeads(leadsResult);
            const totalCountFromResponse = leadsData?.totalCount ?? leadsResult?.[0]?.totalCount;
            let parsedTotalCount = 0;
            if (totalCountFromResponse !== undefined && totalCountFromResponse !== null) {
                const maybeNumber = Number(totalCountFromResponse);
                parsedTotalCount = Number.isNaN(maybeNumber) ? 0 : maybeNumber;
            }
            setTotalLeads(parsedTotalCount);
            setIsInitialLoad(false);

            requestAnimationFrame(() => {
                if (scrollContainerRef.current && scrollPos > 0) {
                    scrollContainerRef.current.scrollTop = scrollPos;
                }
            });
        }
    }, [leadsData, isError]);

    const { data: leadsDataDDL, isLoading: DDLloading } = useGetRequestQuery<any>(
        ['getCrmDDLService'],
        () => getCrmDDLService(),
        { enabled: true }
    );

    useEffect(() => {
        if (leadsDataDDL?.data && !DDLloading) {
            setLeadDropdownData(leadsDataDDL?.data || []);
        }
    }, [leadsDataDDL]);

    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData({ ...formData, [name]: value });
        // Trigger search after value change
        debouncedSearch();
    };

    // Add debounce function
    const debounce = (func: Function, delay: number) => {
        let timeoutId: NodeJS.Timeout;
        const debouncedFn = (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
        debouncedFn.cancel = () => clearTimeout(timeoutId);
        return debouncedFn;
    };

    // Add debounce function
    const debouncedSearch = debounce(() => {
        if (!isSearching) {
            setIsSearching(true);
            setIsInitialLoad(true);
            setFormData(prev => ({ ...prev, pageNumber: 0 }));
            setLeads([]);
            setTimeout(() => refetch(), 0);
            setTimeout(() => setIsSearching(false), 1000);
        }
    }, 500); // 500ms delay

    const handleSearch = () => {
        debouncedSearch();
    };

    const { mutate: manageUser, isPending: isManageUserPending } = useMutationRequestQuery("manageUser", deleteCrmLeadsService, {
        onSuccess: () => {
            toast.success("Lead deleted successfully.");
            queryClient.invalidateQueries({ queryKey: ["getCrmLeads"] });
            setDeleteModal(false);
        },
        onError: () => {
            toast.error("Failed to delete lead.");
            setDeleteModal(false);
        },
    });

    const confirmDelete = (id: number) => {
        setSelectedUserId(id);
        setDeleteModal(true);
    };
 
    const handleDeleteClick = () => {
        if (selectedUserId) {
            manageUser(selectedUserId);
        }
    };

    const { mutate: deleteLeads, isPending: isDeleting } = useMutationRequestQuery(
        "deleteMultipleLeads",
        deleteMultipleLeadsService,
        {
            onSuccess: () => {
                toast.success("Leads deleted successfully.");
                refetch()
                setToggleCleared(true)
                setDeleteMultipleModal(false);
                setSelectedLeadsId([]);
            },
            onError: () => {
                toast.error("Failed to delete leads.");
            }
        }
    );

    const handleDeleteSelectedLeads = () => {
        if (selectedLeadsId.length === 0) {
            toast.error("No leads selected.");
            return;
        }
        setDeleteMultipleModal(true);
    };

    const confirmDeleteMultipleLeads = () => {
        deleteLeads(selectedLeadsId);
    };

    const { mutate: updateFollowUp, isPending: isUpdating } = useMutationRequestQuery(
        "updateLeadFollowUp",
        updateLeadFollowUpService,
        {
            onSuccess: () => {
                toast.success("Follow-up date updated.");
                refetch()
                setFormData({ ...formData, followup: '' })
                setSelectedLeadsId([])
            },
            onError: () => {
                toast.error("Failed to update follow-up date.");
            }
        }
    );

    const handleUpdateFollowUp = () => {
        if (!formData.followup || selectedLeadsId.length === 0) {
            toast.error("Select a follow-up date and at least one lead.");
            return;
        }
        // Format the date to ISO string with time set to 14:00:00
        const followupDate = new Date(formData.followup);
        followupDate.setHours(14, 0, 0, 0);
        const formattedDate = followupDate.toISOString();
        updateFollowUp({ date: formattedDate, data: selectedLeadsId });
    };

    const numericTotalLeads = Number(totalLeads) || 0;
    const totalPages = numericTotalLeads > 0 ? Math.ceil(numericTotalLeads / formData.pageSize) : 1;
    const showRowRange = leads.length > 0 && numericTotalLeads > 0;
    const startLeadIndex = showRowRange ? formData.pageNumber * formData.pageSize + 1 : 0;
    const endLeadIndex = showRowRange ? Math.min(startLeadIndex + leads.length - 1, numericTotalLeads) : 0;

    const handlePageChange = (newPage: number) => {
        if (newPage < 0 || newPage >= totalPages) {
            return;
        }
        setFormData(prev => ({ ...prev, pageNumber: newPage }));
        setLeads([]);
        setIsInitialLoad(true);
        setTimeout(() => refetch(), 0);
    };

    const leadColumns = [
        {
            dataField: "followUp",
            text: "Followup",
            formatter: (cell: string) => {
                if (!cell) return "";
                const followUpDate = new Date(cell);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                let color = "#008000";
                if (followUpDate <= today) {
                    color = "red";
                } else if (followUpDate.getTime() === today.getTime()) {
                    color = "red";
                }
                return (
                    <span style={{ color, fontWeight: "bold" }}>
                        {followUpDate.toLocaleDateString("en-GB")}
                    </span>
                );
            },
            headerStyle: { width: "6rem", color: "#0000ff" },
        },
        { 
            dataField: "leadID", 
            text: "ID", 
            headerStyle: { width: "3rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        { 
            dataField: "staff", 
            text: "Staff", 
            headerStyle: { width: "4rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        { 
            dataField: "stage", 
            text: "Stage", 
            headerStyle: { width: "10rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        {
            dataField: "surname",   
            text: "Surname", 
            formatter: (cell: string, row: any) => (
                <Link to={`/EditLead/${row.leadID}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {cell ? cell.toUpperCase() : ""}
                </Link>
            ),
            headerStyle: { width: "13rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        {
            dataField: "firstName",
            text: "First Name",
            formatter: (cell: string) => cell ? cell.charAt(0).toUpperCase() + cell.slice(1).toLowerCase() : "",
            headerStyle: { width: "8rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        { 
            dataField: "mobile", 
            text: "Mobile",
            headerStyle: { width: "9.5rem", color: "#0000ff" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        {
            dataField: "latestComment",
            text: "Comments",
            formatter: (cell: string) => (
                <div>
                    {cell}
                </div>
            ),
            headerStyle: { minWidth: "22rem", color: "#0000ff", overflow: "hidden", textOverflow: "ellipsis" },
            style: (cell: any, row: any) => {
                if (!row.followUp) return {};
                const followUpDate = new Date(row.followUp);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                followUpDate.setHours(0, 0, 0, 0);
                return {
                    color: followUpDate <= today ? 'red' : 'green'
                };
            }
        },
        {
            dataField: "actions",
            text: "Action",
            formatter: (_: any, row: any) => (
                <ul className="action simple-list d-flex flex-row">
                    <li className="edit">
                        <Link to={`/EditLead/${row.leadID}`}>
                            <i className="icon-pencil-alt" />
                        </Link>
                    </li>
                    {isAdmin ? <li className="delete" style={{ marginLeft: "10px" }}>
                        <a onClick={() => confirmDelete(row.leadID)}>
                            <i className="icon-trash" />
                        </a>
                    </li> : null}

                </ul>
            ),
            headerStyle: { width: '7rem', color: "#0000ff" },
        }
    ];

    const handleDownloadExcel = async (e?: any) => {
        if (e) e.preventDefault();
        try {
            const currentFormData = { ...formData, pageNumber: 0 };
            const response = await getCrmLeadsService(currentFormData) as any;
            const leadsToDownload = response?.data || [];

            if (!leadsToDownload.length) {
                toast.warning("No lead data available to download.");
                return;
            }
            const excelData = leadsToDownload.map((row: any) => ({
                "Lead Number": row.leadID,
                "First Name": row.firstName,
                "Surname": row.surname,
                "Mobile": row.mobile,
                "Email": row.email,
                "Staff": row.staff
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);

            worksheet["!cols"] = [
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 30 },
                { wch: 15 }
            ];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Property Leads Data");
            XLSX.writeFile(workbook, "Property_Leads_Data.xlsx");
        } catch (error) {
            toast.error("Failed to fetch data for download.");
        }
    };

    const selectRow = {
        selectColumnStyle: {
            position: 'absolute',
            right: '0'
        },
        mode: "checkbox",
        clickToSelect: true,
        selected: selectedLeadsId,
        hideSelectAll: true,
        selectionRenderer: ({ checked, disabled }: { checked: boolean; disabled: boolean }) => (
            isAdmin ? (<input
                type="checkbox"
                checked={checked}
                disabled={disabled}
            />) : null
        ),

        onSelect: (row: any, isSelect: boolean) => {
            setSelectedLeadsId((prev: any) =>
                isSelect ? [...prev, row.leadID] : prev.filter((id: any) => id !== row.leadID)
            );
        },
        onSelectAll: (isSelect: boolean, selectedRows: any) => {
            const selectedIds = selectedRows.map((row: any) => row.leadID);
            setSelectedLeadsId(isSelect ? selectedIds : []);
        },
        style: { width: '20px', height: '20px' }
    };

    return (
        <div className="px-4 pt-4" style={{ 
            height: '100vh', 
            overflow: 'hidden', 
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div style={{ 
                borderRadius: '8px', 
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', 
                padding: '10px', 
                backgroundColor: '#fff', 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                    <h3 className="f-w-900 heading-color mx-3 py-2">Property Leads</h3>
                    <h3 className="f-w-900 heading-color mx-3">
                        Total Leads {isInitialLoad ? "-" : totalLeads}
                    </h3>
                    {RoleID !== '2' ? (
                        <Button color="primary" className="mx-3" onClick={() => navigate("/AddLead?stage=LeadsProperty")}>
                            Add Lead
                        </Button>
                    ) : (
                        <Button color="primary" className="mx-3 invisible" style={{ visibility: 'hidden' }}>
                            Add Lead
                        </Button>
                    )}

                </div>
                <Row className="mt-4">
                    <div className="d-md-flex item-center mt-2">
                        <Col className="responsive-col-lead" xs={12} style={{ width: '4.5%', paddingInline: '2px' }}>
                            <Label className="mt-2">Lead ID:</Label>
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '7%', paddingInline: '2px' }}>
                            <TextField
                                label=""
                                type="text"
                                name="leadID"
                                value={formData.leadID}
                                setValue={handleValueChange}
                                placeholder=""
                                design="flex"
                                passCol="col-11"
                                maxChars={6}
                                tabIndex={1}
                                showClearIcon={true}
                                autoComplete="off"
                            />
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '4.7%', paddingInline: '2px' }}>
                            <Label className="mt-2">Surname:</Label>
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                            <TextField
                                label=""
                                type="text"
                                name="surname"
                                value={formData.surname}
                                setValue={handleValueChange}
                                placeholder=""
                                design="flex"
                                passCol="col-11"
                                maxChars={15}
                                tabIndex={2}
                                showClearIcon={true}
                                autoComplete="off"
                            />
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '3.7%', paddingInline: '2px' }}>
                            <Label className="mt-2">Mobile:</Label>
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '12%', paddingInline: '2px' }}>
                            <MobileField
                                label=""
                                mobileField="mobile"
                                value={formData.mobile}
                                setValue={handleValueChange}
                                placeholder="Mobile"
                                design="flex"
                                passCol="col-11"
                                maxLength={18}
                                tabIndex={3}
                                showClearIcon={true}
                                autoComplete="off"
                            />
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '3%', paddingInline: '2px' }}>
                            <Label className="mt-2">Email:</Label>
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '18%', paddingInline: '2px' }}>
                            <TextField
                                label=""
                                type="email"
                                name="email"
                                value={formData.email}
                                setValue={handleValueChange}
                                placeholder=""
                                design="flex"
                                passCol="col-11"
                                maxChars={40}
                                tabIndex={4}
                                showClearIcon={true}
                                autoComplete="off"
                            />
                        </Col>
                        {isAdmin && (
                            <Col className="responsive-col-lead" xs={12} style={{ width: '3%', paddingInline: '2px' }}>
                                <Label className="mt-2">Staff:</Label>
                            </Col>
                        )}
                        {isAdmin && (
                            <Col className="responsive-col-lead" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                                <DropdownWithValueName
                                    label=""
                                    name="staff"
                                    value={formData.staff}
                                    setValue={handleValueChange}
                                    options={leadDropdownData?.staff || []}
                                    required={true}
                                    tabIndex={5}
                                    passCol="col-10"
                                />
                            </Col>
                        )}

                        <Col className="responsive-col-lead" xs={12} style={{ width: '3.7%', paddingInline: '2px' }}>
                            <Label className="mt-2">Source:</Label>
                        </Col>
                        <Col className="responsive-col-lead" xs={12} style={{ width: '8%', paddingInline: '2px' }}>
                            <DropdownWithValueName
                                label=""
                                name="source"
                                value={formData.source}
                                setValue={handleValueChange}
                                options={leadDropdownData?.leadSources || []}
                                required={true}
                                tabIndex={6}
                                passCol="col-10"
                            />
                        </Col>
                        <Button onClick={handleSearch} style={{ fontSize: '16px' }} className="text-center mb-3" color="success">
                            Search Now
                        </Button>
                    </div>
                    {isAdmin && (
                        <div className="d-md-flex">
                            <div>
                                <Link
                                    to="/BulkSMS"
                                    className="btn btn-outline-primary mb-3 mx-1"
                                >
                                    SMS
                                </Link>
                                <Link
                                    to="/BulkChange"
                                    className="btn btn-outline-primary mb-3 mx-1"
                                >
                                    Lead
                                </Link>
                                <Link
                                    to="/BulkstaffChange"
                                    className="btn btn-outline-primary mb-3 mx-1"
                                >
                                    Staff
                                </Link>
                                <Link
                                    to=""
                                    onClick={handleDownloadExcel}
                                    className="btn btn-outline-primary mb-3 mx-1"
                                >
                                    Excel
                                </Link>
                            </div>
                            <div className="d-md-flex">
                                <div className="d-flex">
                                    <Col xs={12} style={{ width: '20%', paddingInline: '5px' }}>
                                        <Label className="mb-3">Date:</Label>
                                    </Col>
                                    <Col xs={12} style={{ width: '70%', paddingInline: '2px' }}>
                                        <SelectDatePicker
                                            label=""
                                            name="followup"
                                            value={formData.followup}
                                            setValue={handleValueChange}
                                            design="flex"
                                            tabIndex={7}
                                        />
                                    </Col>
                                </div>
                                <div>
                                    <Button color="primary" className="mb-3" outline onClick={handleUpdateFollowUp}>Update Followup</Button>
                                    <Button color="danger" className="mb-3" style={{ marginLeft: '7px' }} outline onClick={handleDeleteSelectedLeads}>Delete Leads</Button>
                                    {selectedLeadsId.length > 0 && (
                                        <span 
                                            className="d-inline-flex align-items-center ms-2 mb-3" 
                                            style={{ 
                                                fontSize: '14px', 
                                                padding: '6px 12px', 
                                                border: '1px solid #0052cc',
                                                borderRadius: '50px',
                                                backgroundColor: '#e6f0fa',
                                                color: '#0052cc',
                                                fontWeight: 'bold',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Selected: {selectedLeadsId.length}
                                            <i 
                                                className="icon-brush ms-2" 
                                                style={{ 
                                                    cursor: 'pointer', 
                                                    color: '#ff3f3f', 
                                                    fontSize: '16px',
                                                    transition: 'transform 0.2s, color 0.2s'
                                                }} 
                                                onClick={() => setSelectedLeadsId([])}
                                                title="Deselect all"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#cc0000';
                                                    e.currentTarget.style.transform = 'scale(1.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = '#ff3f3f';
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                }}
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </Row>
                {isLoading ? (
                    <div style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", padding: "12px", backgroundColor: "#fff", textAlign: "center" }} className='mt-4 mb-2'>
                        <Spinner color="primary" />
                    </div>
                ) : leads.length !== 0 ? (
                    <div ref={scrollContainerRef} style={{ 
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        position: 'relative',
                        marginTop: '20px',
                        marginLeft: '-2.5rem' 
                    }}>
                        <div style={{ backgroundColor: "#fff", width: "100%", overflowX: "auto"}}>
                            <BootstrapTable
                                keyField="leadID"
                                data={leads}
                                columns={leadColumns as any}
                                selectRow={selectRow as any}
                                bordered={false}
                                wrapperClasses="table-responsive"
                                noDataIndication="No property leads found"
                            />
                        </div>
                        <div
                            className="d-flex justify-content-between align-items-center"
                            style={{
                                backgroundColor: "#fff",
                                borderTop: "1px solid #e9ecef",
                                padding: "10px 20px"
                            }}
                        >
                            <Button
                                color="secondary"
                                outline
                                disabled={formData.pageNumber === 0 || isLoading}
                                onClick={() => handlePageChange(formData.pageNumber - 1)}
                            >
                                Prev
                            </Button>
                            <div style={{ textAlign: "center" }}>
                                <div>
                                    Page {formData.pageNumber + 1} of {totalPages}
                                </div>
                                {showRowRange && (
                                    <small className="text-muted">
                                        Showing {startLeadIndex} - {endLeadIndex} of {numericTotalLeads}
                                    </small>
                                )}
                            </div>
                            <Button
                                color="secondary"
                                outline
                                disabled={formData.pageNumber >= totalPages - 1 || isLoading}
                                onClick={() => handlePageChange(formData.pageNumber + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", padding: "12px", backgroundColor: "#fff", textAlign: "center" }} className='mt-4 mb-2'>
                        <h6>No Property Leads data available.</h6>
                    </div>
                )}
            </div>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete the selected lead?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isManageUserPending}>
                        {isManageUserPending ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>  
            </Modal>
            <Modal isOpen={deleteMultipleModal} toggle={() => setDeleteMultipleModal(!deleteMultipleModal)} centered>
                <ModalHeader toggle={() => setDeleteMultipleModal(!deleteMultipleModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete the selected {selectedLeadsId.length} leads?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteMultipleModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={confirmDeleteMultipleLeads} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default LeadsProperty; 
